'use client';

import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, CircleAlert, ShieldX } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { createPortal } from 'react-dom';
import IconAdaptive from '@/components/IconAdaptive';

export type ModalAlertVariant = 'danger' | 'warning' | 'info';

export interface ModalAlertProps {
    isOpen: boolean;
    onClose: () => void;
    message: string;
    subMessage?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    onAlert: () => void;
    onCancel?: () => void;
    variant?: ModalAlertVariant;
}

const VARIANT_STYLES: Record<
    ModalAlertVariant,
    {
        icon: LucideIcon;
        iconColor: string;
        confirmColorType: 0 | 1 | 2;
    }
> = {
    danger: {
        icon: ShieldX,
        iconColor: 'text-color-modal-confirm-danger',
        confirmColorType: 0,
    },
    warning: {
        icon: AlertTriangle,
        iconColor: 'text-color-modal-confirm-warning',
        confirmColorType: 0,
    },
    info: {
        icon: CircleAlert,
        iconColor: 'text-color-modal-confirm-info',
        confirmColorType: 0,
    },
};

export default function ModalAlert({ isOpen, message, subMessage, variant = 'info' }: ModalAlertProps) {
    const [modalContainer, setModalContainer] = useState<HTMLElement | null>(null);

    useEffect(() => {
        setModalContainer(document.body);
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!modalContainer) return null;

    const variantStyle = VARIANT_STYLES[variant];

    return createPortal(
        <AnimatePresence mode="wait">
            {isOpen && (
                <motion.div
                    className="fixed inset-0 z-[10000] flex items-center justify-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                >
                    <div className="absolute inset-0 backdrop-blur-sm bg-color-base/40" />
                    <motion.div
                        className="relative z-10 bg-color-white-base rounded-2xl shadow-float-heavy w-[90%] max-w-sm p-6 flex flex-col items-center gap-4"
                        initial={{ opacity: 0, scale: 0.92, y: 8 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.92, y: 8 }}
                        transition={{ duration: 0.25, ease: 'easeOut' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <IconAdaptive
                            Icon={variantStyle.icon}
                            baseSize={50}
                            strokeWidth={2}
                            className={variantStyle.iconColor}
                        />
                        <div className="flex flex-col items-center gap-1 text-center">
                            <p className="font-corp font-bold text-color-base text-base leading-snug">{message}</p>
                            {subMessage && (
                                <p className="font-notojp text-color-co-gray-dark text-xs leading-snug">{subMessage}</p>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>,
        modalContainer,
    );
}
